import { useEffect, useState } from "react";
import { db, type Point, type Contact, type Visit, type Product, type Contractor } from "../db";
import { daysAgo, daysText, dayClass, typeIcon, typeName, ownershipName, fmtDate } from "../lib";
import { Shell, type ScreenProps } from "./common";
import VisitPhotos from "./VisitPhotos";

export default function PointCard({ updateReady, nav, back, state, pointId }: ScreenProps & { pointId: string }) {
  const [point, setPoint] = useState<Point | null>(null);
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [products, setProducts] = useState<Map<string, Product>>(new Map());

  useEffect(() => {
    void (async () => {
      const p = await db.points.get(pointId);
      setPoint(p ?? null);
      if (p?.contractorId) setContractor((await db.contractors.get(p.contractorId)) ?? null);
      const vs = (await db.visits.where("pointId").equals(pointId).toArray())
        .filter((v) => !v.deletedAt)
        .sort((a, b) => b.visitedAt.localeCompare(a.visitedAt));
      setVisits(vs);
      setContacts((await db.contacts.where("pointId").equals(pointId).toArray()).filter((c) => !c.deletedAt));
      const ids = [...new Set(vs.flatMap((v) => v.items.map((i) => i.productId)))];
      const prods = (await db.products.bulkGet(ids)).filter(Boolean) as Product[];
      setProducts(new Map(prods.map((p) => [p.id, p])));
    })();
  }, [pointId, state.lastSync, state.pending]);

  if (!point) {
    return <Shell title="Точка" onBack={back} state={state} updateReady={updateReady}><div className="empty">Загрузка…</div></Shell>;
  }

  const last = visits[0];
  const d = daysAgo(last?.visitedAt ?? null);

  return (
    <Shell
      title={point.name}
      onBack={back}
      state={state} updateReady={updateReady}
      footer={
        <>
          <button className="btn primary" onClick={() => nav({ name: "visitForm", pointId })}>
            Новый визит
          </button>
          <button className="btn" onClick={() => nav({ name: "pointForm", id: pointId })}>Изменить</button>
          <button className="btn" onClick={() => nav({ name: "contactForm", pointId })}>+ Контакт</button>
        </>
      }
    >
      <div className="card">
        <h2>{point.name}</h2>
        <div className="hint">
          {point.city}{point.district ? ", " + point.district + " р-н" : ""}, {point.address}
        </div>
        <div className="tags">
          <span className="tag">{typeIcon(point)} {typeName(point.type)}</span>
          {ownershipName(point.ownership) &&
            <span className="tag">{ownershipName(point.ownership)}</span>}
          <span className="tag">Категория {point.category}</span>
          <span className="tag">{point.status}</span>
          <span className="tag">{point.network ? "сеть " + point.network : "одиночная"}</span>
          {contractor && <span className="tag">контрагент: {contractor.name}</span>}
          <span className={"pill " + dayClass(point, d)} style={{ alignSelf: "center" }}>
            {d === null ? "не были ни разу" : daysText(d) + " назад"}
          </span>
        </div>
      </div>

      {last ? (
        <div className="last">
          <div style={{ display: "flex", gap: 9, alignItems: "baseline", flexWrap: "wrap", marginBottom: 9 }}>
            <span className="eyebrow">Последний визит</span>
            <span className="hint">{fmtDate(last.visitedAt)}</span>
          </div>
          <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.6 }}>{last.summary}</p>
          {last.items.length > 0 && (
            <dl className="kv">
              <dt>Оставил</dt>
              <dd>{last.items.map((i) =>
                `${products.get(i.productId)?.name ?? "позиция"} — ${i.qty} шт.`).join("; ")}</dd>
            </dl>
          )}
          {last.agreement && (
            <dl className="kv"><dt>Договорились</dt><dd>{last.agreement}</dd></dl>
          )}
          {last.needsReview && (
            <div className="err">
              Требует сверки: списано больше, чем числится на руках.
            </div>
          )}
          {/* Фото последнего визита показываем сразу: это то, ради чего
              руководитель открывает точку. У истории ниже — по кнопке. */}
          <VisitPhotos visit={last} eager />
          {/* Фото часто делают телефоном и переносят на планшет позже —
              значит, к сохранённому визиту нужно уметь вернуться. */}
          <button className="btn tiny" onClick={() => nav({ name: "visitForm", pointId, visitId: last.id })}>
            Дополнить визит
          </button>
        </div>
      ) : (
        <div className="note">Визитов ещё не было. Это первое посещение точки.</div>
      )}

      {contacts.length > 0 && (
        <>
          <div className="sub">Контактные лица</div>
          {contacts.map((c) => (
            <dl className="kv" key={c.id}>
              <dt>{c.position}</dt>
              <dd>
                {c.fullName}
                {(c.phone || c.email) && (
                  <div className="hint">{[c.phone, c.email].filter(Boolean).join(" · ")}</div>
                )}
              </dd>
            </dl>
          ))}
        </>
      )}

      {visits.length > 1 && (
        <>
          <div className="sub">Предыдущие визиты</div>
          <div className="hint" style={{ marginBottom: 4 }}>Нажми на визит, чтобы его дополнить</div>
          {visits.slice(1).map((v) => (
            <div key={v.id}>
              <button className="vrow"
                onClick={() => nav({ name: "visitForm", pointId, visitId: v.id })}>
                <span className="hint" style={{ minWidth: 82, flex: "none" }}>{fmtDate(v.visitedAt)}</span>
                <span style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.55 }}>{v.summary}</span>
              </button>
              <VisitPhotos visit={v} />
            </div>
          ))}
        </>
      )}
    </Shell>
  );
}
